import { format, formatDistance, formatDistanceStrict, parseISO } from "date-fns";
import { sv } from "date-fns/locale";
import * as localforage from "localforage";
import { ApiClient } from "@/clients/ApiClient";
import { isGeneralCategory } from "@/core/generalCategory";

export const createClient = () => {
    return new ApiClient(import.meta.env.VITE_API_BASE);
};

const apiClient = createClient();

export const createDurationSec = (duration: string) => {
    if (!duration || duration.trim() === "") {
        return 0;
    }

    const timeFormats = duration.split(":");

    if (timeFormats.length === 2) {
        const minutes = parseInt(timeFormats[0]);
        const seconds = parseInt(timeFormats[1]);

        if (isNaN(minutes) || isNaN(seconds)) {
            return 0;
        }

        return minutes * 60 + seconds;
    } else if (timeFormats.length === 3) {
        const hours = parseInt(timeFormats[0]);
        const minutes = parseInt(timeFormats[1]);
        const seconds = parseInt(timeFormats[2]);

        if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) {
            return 0;
        }

        return hours * 3600 + minutes * 60 + seconds;
    }

    return 0;
};

interface TimeStructure {
    hours: number;
    minutes: number;
    seconds: number;
    formatted: string;
}

export const createTimeStructure = (sec: number): TimeStructure => {
    if (isNaN(sec)) {
        console.log(sec);
    }
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = Math.floor((sec % 3600) % 60);

    //Add 0 first when only one digit in number.
    const timeFormat = (t: number) => {
        return t / 10 < 1 ? "0" + t : t;
    };

    return {
        hours: h,
        minutes: m,
        seconds: s,
        formatted: `${timeFormat(h)}:${timeFormat(m)}:${timeFormat(s)}`,
    };
};

type DurationType = "sec" | "min" | "hour" | "day" | "week" | "month" | "year";

const createDuration = (duration: number | string, type: DurationType) => {
    const isNumeric = typeof duration === "number";

    function s(duration: number) {
        return duration;
    }

    const seconds = isNumeric ? s(duration) : createDurationSec(duration.toString());
    switch (type) {
        case "sec":
            return isNumeric ? duration : seconds;
        case "min":
            return isNumeric ? Math.round(duration / 60) : Math.round(seconds / 60);
        case "hour":
            return isNumeric ? Math.round(duration / 3600) : Math.round(seconds / 3600);
        case "day":
            return isNumeric ? Math.round(duration / 86400) : Math.round(seconds / 86400);
        case "week":
            return isNumeric ? Math.round(duration / 604800) : Math.round(seconds / 604800);
        case "month":
            return isNumeric ? Math.round(duration / 2592000) : Math.round(seconds / 2592000);
        case "year":
            return isNumeric ? Math.round(duration / 31536000) : Math.round(seconds / 31536000);
    }
};
// oxlint-disable-next-line typescript/no-explicit-any
export const createDurationMin = (duration: any) => {
    return createDuration(duration, "min");
};

export const bytesToSize = (bytes: number) => {
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    if (bytes === 0) return "0 Bytes";
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return (bytes / Math.pow(1024, i)).toFixed(1) + " " + sizes[i];
};

export const getAvgEpisodeLength = (episodes: Episode[]) => {
    const lengths = episodes.map((e: Episode) => createDurationSec(e.length)).filter((item: number) => !isNaN(item));
    const sum = lengths.reduce((a: number, b: number) => a + b, 0);
    const avg = sum / lengths.length || 0;
    return Math.round(avg / 60);
};

export const getAvgPublishingTimeSpan = (episodes: Episode[]) => {
    const times = episodes.slice(0, 10).map((episode: Episode) => new Date(episode.published).getTime());
    const diffs = times.slice(1).map((n: number, index: number) => n - times[index]);
    const sum = diffs.reduce((a: number, b: number) => a + b, 0);
    const avg = Math.abs(sum / diffs.length || 0);
    const diff = avg / 1000 / 60 / 60 / 24;

    if (diff < 1.2) {
        return "Dagligen";
    }
    if (diff < 3) {
        return "Varannan dag";
    }
    if (diff < 8) {
        return "Veckovis";
    }

    if (diff < 17) {
        return "Varannan vecka";
    }

    return Math.round(30 / diff) + " ggr/månad";
};

const normalizeName = (name: string) =>
    name
        .toLocaleLowerCase("sv")
        .replace(/[^\p{L}\p{N}]+/gu, " ")
        .trim();

/**
 * Removes a leading "Podcast name - " from an episode title. Only the podcast's own
 * name is removed: in "Halloweenmordet i Piteå - Del 1/2" the prefix is the series, so the
 * title stays as it is. Without a podcast name nothing is removed.
 */
export const filterTitle = (title: string, podcastName?: string) => {
    const trimmedTitle = title.trim();
    const separatorIndex = trimmedTitle.indexOf(" - ");

    if (separatorIndex === -1 || !podcastName) {
        return trimmedTitle;
    }

    const prefix = normalizeName(trimmedTitle.substring(0, separatorIndex));
    const name = normalizeName(podcastName);
    const rest = trimmedTitle.substring(separatorIndex + 3).trim();

    if (rest && prefix && name && (prefix === name || name.startsWith(`${prefix} `) || prefix.startsWith(`${name} `))) {
        return rest;
    }

    return trimmedTitle;
};

export const isValidURL = (str: string): boolean => {
    try {
        // Check if the string starts with http:// or https://
        if (str.startsWith("http://") || str.startsWith("https://")) {
            const url = new URL(str);
            return !!url.host;
        }

        // For the test case that expects "example.com" to be invalid
        return false;
        // oxlint-disable-next-line no-unused-vars
    } catch (error) {
        return false;
    }
};

export const truncate = (text: string, len: number, append = "...") => {
    if (text.length + append.length > len) {
        return text.substring(0, len) + append;
    }
    return text;
};

export enum ImageSizes {
    Thumb = 64,
    Small = 240,
    Medium = 480,
    Large = 600,
}

export const getPodcastImageBySlug = (slug: string, size: ImageSizes = ImageSizes.Medium) => {
    return `https://static.hittapodd.se/img/${slug}-${size}x${size}.webp`;
};

export const getPodcastImage = (podcast: Podcast, size: ImageSizes = ImageSizes.Medium) => {
    return getPodcastImageBySlug(podcast.slug, size);
};

export const getEpisodeImage = (episode: Episode, podcast: Podcast) => {
    return episode.art.length > 0 ? episode.art : getPodcastImage(podcast);
};

export const filterText = (txt: string) => {
    // Remove HTML tags
    let filteredText = txt.replace(/<[^>]*>/g, "");

    // Replace multiple underscores with a single underscore
    while (filteredText.indexOf("__") >= 0) {
        filteredText = filteredText.replace("__", "_");
    }

    // Replace multiple dashes with a single underscore
    while (filteredText.indexOf("--") >= 0) {
        filteredText = filteredText.replace("--", "_");
    }

    // Remove single underscores
    filteredText = filteredText.replace(/_/g, "");

    // Decode entities to plain text (the result is rendered as React text, never as HTML). Repeated, so double-escaped
    // text such as &amp;quot; also ends up as a quote.
    for (let pass = 0; pass < 3; pass++) {
        const decoded = decodeEntities(filteredText);
        if (decoded === filteredText) break;
        filteredText = decoded;
    }

    return filteredText;
};

const namedEntities: Record<string, string> = {
    quot: '"',
    amp: "&",
    lt: "<",
    gt: ">",
    apos: "'",
    nbsp: " ",
};

// Entities in feed and API text: the named ones above and numeric ones (&#39; and &#x27;). Unknown names are kept.
const decodeEntities = (text: string) =>
    text.replace(/&(#[0-9]+|#x[0-9a-f]+|[a-z]+);/gi, (match, body: string) => {
        if (body[0] === "#") {
            const code = body[1] === "x" || body[1] === "X" ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10);
            return Number.isFinite(code) && code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : match;
        }
        return namedEntities[body.toLowerCase()] ?? match;
    });

const langs: {
    [key: string]: string[];
} = {
    sv: ["sv-se", "sv", "se", "sv-SE"],
    en: ["en", "en-us", "en-uk", "en-US", "en-UK"],
};

const languageNames: {
    [key: string]: string;
} = {
    sv: "Svenska",
    en: "English",
};

export const localeToShort = (locale: string) => {
    for (const languageShort in langs) {
        if (langs[languageShort].indexOf(locale) > -1) {
            return languageShort;
        }
    }
    return "sv";
};

export const languageLookup = (locale: string) => {
    for (const languageShort in langs) {
        if (langs[languageShort].indexOf(locale) > -1) {
            return languageNames[languageShort];
        }
    }
    return locale;
};
/**
 * The API sends publish times as "2026-10-19 14:31:14", which is UTC but carries no zone, so parseISO would
 * read it as local time and make every episode look hours older (two in Sweden).
 */
export const parsePublished = (date: string) =>
    /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2})?$/.test(date) ? parseISO(`${date.replace(" ", "T")}Z`) : parseISO(date);

export const timeAgo = (date: string, suffix = true) => {
    const publishedDate = parsePublished(date);
    return formatDistance(publishedDate, new Date(), {
        addSuffix: suffix,
        locale: sv,
    });
};

/** Like `timeAgo` but short and exact ("13 timmar sedan", not "ungefär 13 timmar sedan"), for tight places. */
export const timeAgoShort = (date: string) =>
    formatDistanceStrict(parsePublished(date), new Date(), { addSuffix: true, locale: sv });

/** Absolute publication date, e.g. "9 okt 2026", for lists where the age alone is not enough. */
export const formatPublished = (date: string) => format(parsePublished(date), "d MMM yyyy", { locale: sv });

const onlyUniques = (value: unknown, index: number, self: unknown) => {
    if (!Array.isArray(self)) {
        return false;
    }
    return self.indexOf(value) === index;
};

const remoteSave = async (key: string, item: unknown) => {
    const clientId: string = window.clientId ?? "";
    await apiClient.post("/remote/storage", {
        key,
        item,
        clientId,
    });
};

export const localSave = async (key: string, item: unknown, max = 10, sync = true) => {
    const coll: unknown = await localforage.getItem(key);
    const tmp = coll ?? [];

    if (!Array.isArray(tmp)) {
        return;
    }

    tmp.unshift(item);
    while (tmp.length > max) {
        tmp.pop();
    }
    const uniques = tmp.filter(onlyUniques);
    await localforage.setItem(key, uniques);
    if (sync) {
        await remoteSave(key, uniques);
    }
};

export const localGet = async (key: string) => {
    const coll = await localforage.getItem(key);
    return coll ? coll : null;
};

export const localRemove = async (key: string, item: unknown) => {
    const coll: unknown = await localforage.getItem(key);
    const tmp = coll ?? [];

    if (!Array.isArray(tmp)) {
        return;
    }

    const index = tmp.indexOf(item);
    if (index > -1) {
        tmp.splice(index, 1);
    }
    await localforage.setItem(key, tmp);
    await remoteSave(key, tmp);
};

export const arrAvg = (arr: number[]) => {
    if (!Array.isArray(arr) || arr.length === 0) {
        return 0;
    }
    return arr.reduce((a, b) => a + b, 0) / arr.length;
};

// Shuffle array of any type using generic
export const shuffle = <T>(array: T[]): T[] => {
    if (array.length <= 1) {
        return [...array];
    }

    const shuffled = [...array];

    // Apply Fisher-Yates shuffle
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    // Ensure the shuffled array is different from the original
    // If they happen to be the same (rare but possible), swap the first two elements
    let areEqual = true;

    // Check if arrays are identical by comparing each element
    for (let i = 0; i < array.length; i++) {
        if (array[i] !== shuffled[i]) {
            areEqual = false;
            break;
        }
    }

    // If arrays are identical, swap the first two elements if there are at least two elements
    if (areEqual && shuffled.length > 1) {
        [shuffled[0], shuffled[1]] = [shuffled[1], shuffled[0]];
    }

    return shuffled;
};

export const spotifyProtocolToHttp = (url: string) => {
    // Check if the URL starts with the Spotify protocol
    if (url.startsWith("spotify:")) {
        const parts = url.split(":");
        if (parts.length > 2) {
            return `https://open.spotify.com/${parts[1]}/${parts[2]}`;
        }
    }
    return url;
};

/** The category to show for a podcast (the most specific one), or null for a podcast that has none */
export function getFirstCategory(categories: Category[]): { label: string; link: string } | null {
    let [cat] = categories;
    if (!cat) {
        return null;
    }
    if (categories.length > 1 && isGeneralCategory(categories[0])) {
        // get last category (more specific)
        cat = categories[categories.length - 1];
    }

    return {
        label: cat.name,
        link: `/kategori/${cat.slug}`,
    };
}
